import React, { useState, useEffect, useRef } from 'react';
import ProductCard from '../../components/ProductCard';
import ScanBillPanel from '../../components/ScanBillPanel';
import { API_BASE_URL, getApiUrl } from '../../config/api'; // Import API_BASE_URL and getApiUrl
import profileDefault from '../../assets/profile_default.png';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { cartSizeLabel } from '../../utils/productSize';

const backgroundUrl = 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=1500&q=80';
const defaultAvatar = (
  <div className="relative w-20 h-20 flex items-center justify-center">
    <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-pink-500 via-pink-400 to-indigo-400 animate-gradient-x" style={{ filter: 'blur(2px)' }} />
    <div className="relative w-16 h-16 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-lg">
      <svg viewBox="0 0 128 128" className="w-12 h-12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="64" cy="64" r="64" fill="#F3F4F6" />
        <ellipse cx="64" cy="54" rx="28" ry="28" fill="#F472B6" />
        <ellipse cx="64" cy="104" rx="40" ry="20" fill="#F3F4F6" />
        <ellipse cx="64" cy="54" rx="20" ry="20" fill="#EC4899" />
        <ellipse cx="64" cy="54" rx="12" ry="12" fill="#fff" />
      </svg>
    </div>
  </div>
);

const ORDER_STATUSES = [
  'Order Placed',
  'Packed',
  'Shipped',
  'Out for Delivery',
  'Delivered',
  'Cancelled'
];

const getStatusColor = (status) => {
  const effectiveStatus = status === 'Pending' ? 'Order Placed' : status;
  switch (effectiveStatus) {
    case 'Delivered':
      return 'bg-green-100 text-green-800';
    case 'Cancelled':
      return 'bg-red-100 text-red-800';
    case 'Order Placed':
      return 'bg-blue-100 text-blue-800';
    case 'Packed':
      return 'bg-purple-100 text-purple-800';
    case 'Shipped':
      return 'bg-yellow-100 text-yellow-800';
    case 'Out for Delivery':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusProgress = (status) => {
  const effectiveStatus = status === 'Pending' ? 'Order Placed' : status;
  const index = ORDER_STATUSES.indexOf(effectiveStatus);
  if (status === 'Cancelled') return 0;
  if (index === -1) return 0; 
  return ((index + 1) / ORDER_STATUSES.length) * 100;
};

const Profile = () => {
  const { user: authUser } = useAuth() || {};
  const isAdmin = Boolean(authUser?.isAdmin);
  const [user, setUser] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    password: '',
    profileImage: '',
    address: '',
    createdAt: ''
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState('');
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef();
  const sampleProfileImage = profileDefault;

  // State for orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersError, setOrdersError] = useState(null);

  // State for review modal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewingItem, setReviewingItem] = useState(null); // { order, item }
  const [submittingReview, setSubmittingReview] = useState(false);
  const [editingReview, setEditingReview] = useState(null); // review object if editing

  // Review form state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');

  // State for user's reviews
  const [userReviews, setUserReviews] = useState([]);

  // State for rental orders
  const [myRentalOrders, setMyRentalOrders] = useState([]);
  const [ownerRentalOrders, setOwnerRentalOrders] = useState([]);
  const [loadingRentals, setLoadingRentals] = useState(true);
  const [rentalError, setRentalError] = useState('');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  // State for rental submissions
  const [myRentalSubmissions, setMyRentalSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [submissionsError, setSubmissionsError] = useState('');

  // Check if user has already reviewed a product
  const hasReviewed = (productId) => {
    const result = userReviews.some(
      review =>
        review.product === productId ||
        (review.product && review.product._id === productId)
    );
    console.log('Checking hasReviewed for productId:', productId, 'Result:', result, 'userReviews:', userReviews);
    return result;
  };

  // Fetch user's reviews
  const fetchUserReviews = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(getApiUrl('/api/products/reviews/recent?all=true'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter reviews by current user
        const userData = JSON.parse(localStorage.getItem('user'));
        const userReviews = data.filter(review => review.user._id === userData._id);
        setUserReviews(userReviews);
      }
    } catch (error) {
      console.error('Error fetching user reviews:', error);
    }
  };

  // Handle opening the modal (add or edit)
  const openReviewModal = (order, item, review = null) => {
    setReviewingItem({ order, item });
    if (review) {
      setReviewRating(review.rating);
      setReviewComment(review.comment);
      setEditingReview(review);
    } else {
      setReviewRating(0);
      setReviewComment('');
      setEditingReview(null);
    }
    setReviewModalOpen(true);
  };

  // Handle closing the modal
  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setReviewingItem(null);
  };

  // Handle review submit
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (reviewRating === 0) {
      alert('Please select a rating');
      return;
    }
    setSubmittingReview(true);
    const token = localStorage.getItem('token');
    try {
      let response;
      if (editingReview) {
        // Edit existing review
        console.log('Editing review:', editingReview);
        const reviewId = editingReview._id || editingReview.id || editingReview.reviewId;
        response = await fetch(getApiUrl(`/api/products/${reviewingItem.item.product._id}/reviews/${reviewId}`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ rating: reviewRating, comment: reviewComment })
        });
      } else {
        // Add new review
        const formData = new FormData();
        formData.append('rating', reviewRating);
        formData.append('comment', reviewComment);
        response = await fetch(getApiUrl(`/api/products/${reviewingItem.item.product._id}/reviews`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }
      if (response.ok) {
        alert(editingReview ? 'Review updated successfully!' : 'Review submitted successfully!');
        closeReviewModal();
        await fetchUserReviews();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      setUser({
        name: userData.name || '',
        email: userData.email || '',
        mobileNumber: userData.mobileNumber || '',
        password: '',
        profileImage: userData.profileImage || '',
        address: userData.address || '',
        createdAt: userData.createdAt || ''
      });
      setPreview(userData.profileImage || '');
    }

    // Fetch orders and user reviews
    const fetchData = async () => {
      setLoadingOrders(true);
      setOrdersError(null);
      const token = localStorage.getItem('token');
      const userId = userData?._id; // Get user ID from localStorage

      if (!userId || !token) {
        setOrdersError('User not logged in or ID missing.');
        setLoadingOrders(false);
        return;
      }

      try {
        const response = await fetch(getApiUrl(`/api/cart/my-orders`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.success) {
          setOrders(data.orders);
        } else {
          setOrdersError(data.message || 'Failed to fetch orders.');
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setOrdersError('Could not load orders. Please try again.');
      } finally {
        setLoadingOrders(false);
      }
      await fetchUserReviews();
    };

    fetchData();
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    const fetchRentals = async () => {
      setLoadingRentals(true);
      setRentalError('');
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        // Use getApiUrl to ensure correct base URL
        const [myRes, ownerRes] = await Promise.all([
          axios.get(getApiUrl('/api/products/rentals/orders/my'), { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(getApiUrl('/api/products/rentals/orders/owner'), { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setMyRentalOrders(myRes.data);
        setOwnerRentalOrders(ownerRes.data);
      } catch (err) {
        setRentalError('Failed to load rental orders.');
      } finally {
        setLoadingRentals(false);
      }
    };
    fetchRentals();
  }, []);

  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoadingSubmissions(true);
      setSubmissionsError('');
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await axios.get(getApiUrl('/api/products/rentals/my-submissions'), { headers: { Authorization: `Bearer ${token}` } });
        setMyRentalSubmissions(res.data);
      } catch (err) {
        setSubmissionsError('Failed to load your rental submissions.');
      } finally {
        setLoadingSubmissions(false);
      }
    };
    fetchSubmissions();
  }, []);

  const handleRentalStatus = async (orderId, status) => {
    setUpdatingOrderId(orderId);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`/api/products/rentals/orders/${orderId}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setOwnerRentalOrders((prev) => prev.map(o => o._id === orderId ? { ...o, status } : o));
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setPreview(file ? URL.createObjectURL(file) : user.profileImage || '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  const handleSave = async () => {
    const updatedUser = { ...user };
    if (image) {
      updatedUser.profileImage = preview;
    }
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setEditing(false);
  };

  const [activeTab, setActiveTab] = useState('orders');

  return (
    <div className="min-h-screen w-full bg-gray-50 py-10 px-2 md:px-10">
      {isAdmin ? (
        <div className="mb-8">
          <ScanBillPanel />
        </div>
      ) : null}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        {/* Profile Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center h-full">
          {/* Circular Profile Image Upload */}
          <div className="relative mb-6 flex flex-col items-center">
            <div className="relative w-28 h-28 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-pink-500 via-pink-400 to-indigo-400 animate-gradient-x" style={{ filter: 'blur(2px)' }} />
              {preview ? (
                <img
                  src={preview}
                  alt="Profile"
                  className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg bg-white"
                />
              ) : (
                <img
                  src={sampleProfileImage}
                  alt="Default Profile"
                  className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg bg-white"
                />
              )}
              {editing && (
                <>
                  <button
                    type="button"
                    className="absolute bottom-2 right-2 bg-pink-500 hover:bg-pink-600 text-white rounded-full p-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-400"
                    onClick={() => fileInputRef.current.click()}
                    aria-label="Edit profile image"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2H7a2 2 0 01-2-2v-1a2 2 0 012-2h1a2 2 0 012 2v1a2 2 0 01-2 2z" /></svg>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </>
              )}
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Profile</h3>
          <form className="space-y-6 w-full">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={user.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow-sm transition"
                disabled={!editing}
              />
            </div>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={user.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow-sm transition"
                disabled={!editing}
              />
            </div>
            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
              <input
                type="tel"
                name="mobileNumber"
                value={user.mobileNumber}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow-sm transition"
                disabled={!editing}
              />
            </div>
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={user.password || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow-sm transition"
                disabled={!editing}
                placeholder={editing ? 'Enter new password' : '********'}
              />
            </div>
            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                name="address"
                value={user.address}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow-sm transition"
                disabled={!editing}
              />
            </div>
            {/* Created At (display only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Created</label>
              <input
                type="text"
                value={user.createdAt ? new Date(user.createdAt).toLocaleString() : ''}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-100"
                disabled
              />
            </div>
            <div className="flex justify-end space-x-2">
              {editing ? (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-6 py-2 bg-gradient-to-r from-pink-500 to-indigo-500 text-white rounded-full font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition"
                    disabled={uploading}
                  >
                    {uploading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-full font-semibold shadow hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="px-6 py-2 bg-gradient-to-r from-pink-500 to-indigo-500 text-white rounded-full font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </form>
          {uploadError && <p className="text-red-500 mt-2">{uploadError}</p>}
        </div>
        {/* Orders & Rentals Card with Tabs */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-4 sm:p-6 lg:p-8 flex flex-col items-start h-full w-full">
          <div className="flex flex-wrap border-b mb-4 sm:mb-6 w-full gap-1">
            <button className={`px-2 sm:px-4 py-2 text-sm sm:text-base font-semibold focus:outline-none whitespace-nowrap ${activeTab === 'orders' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`} onClick={() => setActiveTab('orders')}>Recent Orders</button>
            <button className={`px-2 sm:px-4 py-2 text-sm sm:text-base font-semibold focus:outline-none whitespace-nowrap ${activeTab === 'myRentals' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`} onClick={() => setActiveTab('myRentals')}>My Rental Requests</button>
            <button className={`px-2 sm:px-4 py-2 text-sm sm:text-base font-semibold focus:outline-none whitespace-nowrap ${activeTab === 'ownerRentals' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`} onClick={() => setActiveTab('ownerRentals')}>Requests for My Items</button>
            <button className={`px-2 sm:px-4 py-2 text-sm sm:text-base font-semibold focus:outline-none whitespace-nowrap ${activeTab === 'myRentalSubmissions' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`} onClick={() => setActiveTab('myRentalSubmissions')}>My Rental Submissions</button>
          </div>
          {activeTab === 'orders' && (
            <div className="w-full">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2"><span className="text-pink-500">🛒</span>Recent Orders</h3>
              {loadingOrders ? (
                <div className="flex items-center justify-center w-full h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
                </div>
              ) : ordersError ? (
                <div className="text-red-500 w-full text-center">{ordersError}</div>
              ) : orders.length === 0 ? (
                <div className="text-gray-500 w-full text-center">No orders found. Place an order to see it here!</div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:gap-6 w-full">
                  {orders.map(order => (
                    <div key={order._id} className="bg-gray-50 p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-2 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-base sm:text-lg text-gray-800">Order #{order._id.slice(-6)}</span>
                          {/* Payment Status Badge */}
                          {order.paymentMethod === 'razorpay' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">Paid</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">Unpaid</span>
                          )}
                          {/* Payment Method */}
                          {order.paymentMethod && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                              {order.paymentMethod.charAt(0).toUpperCase() + order.paymentMethod.slice(1).replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">Placed on: {new Date(order.createdAt).toLocaleDateString()}</span>
                      </div>
                      
                      {/* Order Progress */}
                      {order.status !== 'Cancelled' && (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1 overflow-x-auto">
                            {ORDER_STATUSES.map((status, index) => (
                              <div
                                key={status}
                                className={`flex flex-col items-center min-w-0 flex-shrink-0 ${
                                  ORDER_STATUSES.indexOf(order.status) >= index ? 'text-pink-600' : ''
                                }`}
                              >
                                <div
                                  className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mb-1 ${
                                    ORDER_STATUSES.indexOf(order.status) >= index ? 'bg-pink-600' : 'bg-gray-300'
                                  }`}
                                />
                                <span className="text-center text-xs">{status}</span>
                              </div>
                            ))}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1 sm:h-1.5">
                            <div
                              className="bg-pink-600 h-1 sm:h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${getStatusProgress(order.status)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="space-y-3">
                        {order.items.map(item => {
                          const userReview = userReviews.find(r => (r.product === item.product?._id || (r.product && r.product._id === item.product?._id)));
                          return (
                          <div key={item._id} className="flex items-center gap-3 sm:gap-4 bg-white p-3 rounded-md shadow-xs">
                            <a href={`https://www.thevastra.store/product/${item.product?._id}`} className="block flex-shrink-0" tabIndex={0} aria-label={`View details for ${item.name}`}> 
                              <img
                                src={item.product?.images?.[0] || 'https://via.placeholder.com/60'}
                                alt={item.name}
                                className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md hover:scale-105 transition-transform duration-150"
                              />
                            </a>
                            <div className="flex-1 min-w-0">
                              <a href={`https://www.thevastra.store/product/${item.product?._id}`} className="font-medium text-gray-900 hover:text-pink-600 transition-colors text-sm sm:text-base" tabIndex={0} aria-label={`View details for ${item.name}`}>{item.name}</a>
                              <p className="text-xs sm:text-sm text-gray-600">Qty: {item.quantity}</p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {cartSizeLabel(item.size) ? `Size: ${cartSizeLabel(item.size)}, ` : ''}
                                Color: {item.color && item.color.toLowerCase() !== 'default' ? item.color : 'N/A'}
                              </p>
                                {/* Add Review/Edit Review logic for delivered orders */}
                                {order.status === 'Delivered' && (
                                  userReview ? (
                                    <div className="mt-2 p-2 bg-pink-50 rounded-lg">
                                      <div className="flex items-center gap-1 sm:gap-2 mb-1">
                                        {[1,2,3,4,5].map(star => (
                                          <span key={star} className={`text-sm sm:text-base ${star <= userReview.rating ? 'text-pink-500' : 'text-gray-300'}`}>★</span>
                                        ))}
                                        <span className="ml-1 sm:ml-2 text-xs text-gray-500">Your Review</span>
                                      </div>
                                      <div className="text-xs sm:text-sm text-gray-700 italic mb-1">{userReview.comment}</div>
                                      <button
                                        className="px-2 sm:px-3 py-1 text-xs bg-gradient-to-r from-pink-500 to-indigo-500 text-white rounded-full font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition"
                                        onClick={() => openReviewModal(order, item, userReview)}
                                      >
                                        Edit Review
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className="mt-2 px-3 sm:px-4 py-1 bg-gradient-to-r from-pink-500 to-indigo-500 text-white rounded-full text-xs sm:text-sm font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition"
                                      onClick={() => openReviewModal(order, item)}
                                    >
                                      Add Review
                                    </button>
                                  )
                                )}
                            </div>
                            <span className="font-bold text-base sm:text-lg text-pink-600 flex-shrink-0">₹{item.price * item.quantity}</span>
                          </div>
                          );
                        })}
                      </div>
                      <div className="text-right mt-4 font-bold text-lg sm:text-xl text-gray-900">
                        Total: ₹{order.total}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === 'myRentals' && (
            <div className="w-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2"><span className="text-indigo-500">🏷️</span>My Rental Requests</h3>
              {loadingRentals ? (
                <div className="flex flex-col items-center justify-center h-32 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-600"></div>
                </div>
              ) : rentalError ? (
                <div className="flex flex-col items-center justify-center h-32 w-full bg-red-50 rounded-xl shadow-inner">
                  <span className="text-3xl text-red-400 mb-2">⚠️</span>
                  <p className="text-red-500 font-medium">Failed to load rental orders.</p>
                </div>
              ) : myRentalOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 w-full bg-gray-50 rounded-xl shadow-inner">
                  <span className="text-4xl mb-2">📦</span>
                  <p className="text-gray-500 font-medium">No rental requests yet.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4 w-full">
                  {myRentalOrders.map(order => (
                    <div key={order._id} className="bg-white rounded-xl shadow p-4 flex items-center gap-4 hover:shadow-lg transition">
                      <img src={order.rentalProduct?.images?.[0] || '/logo.png'} className="w-16 h-16 rounded-lg object-cover border" alt={order.rentalProduct?.name} />
                      <div className="flex-1">
                        <div className="font-semibold">{order.rentalProduct?.name}</div>
                        <div className="text-xs text-gray-500">Period: {new Date(order.rentalPeriod.startDate).toLocaleDateString()} - {new Date(order.rentalPeriod.endDate).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-500">Status: <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${order.status === 'Approved' ? 'bg-green-100 text-green-700' : order.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{order.status}</span></div>
                      </div>
                      <div className="font-bold text-pink-600">₹{order.total}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === 'ownerRentals' && (
            <div className="w-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2"><span className="text-indigo-500">📥</span>Requests for My Items</h3>
              {loadingRentals ? (
                <div className="flex flex-col items-center justify-center h-32 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-600"></div>
                </div>
              ) : rentalError ? (
                <div className="flex flex-col items-center justify-center h-32 w-full bg-red-50 rounded-xl shadow-inner">
                  <span className="text-3xl text-red-400 mb-2">⚠️</span>
                  <p className="text-red-500 font-medium">Failed to load rental orders.</p>
                </div>
              ) : ownerRentalOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 w-full bg-gray-50 rounded-xl shadow-inner">
                  <span className="text-4xl mb-2">📦</span>
                  <p className="text-gray-500 font-medium">No requests for your items yet.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4 w-full">
                  {ownerRentalOrders.map(order => (
                    <div key={order._id} className="bg-white rounded-xl shadow p-4 flex items-center gap-4 hover:shadow-lg transition">
                      <img src={order.rentalProduct?.images?.[0] || '/logo.png'} className="w-16 h-16 rounded-lg object-cover border" alt={order.rentalProduct?.name} />
                      <div className="flex-1">
                        <div className="font-semibold">{order.rentalProduct?.name}</div>
                        <div className="text-xs text-gray-500">Period: {new Date(order.rentalPeriod.startDate).toLocaleDateString()} - {new Date(order.rentalPeriod.endDate).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-500">Renter: {order.renter?.name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">Status: <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${order.status === 'Approved' ? 'bg-green-100 text-green-700' : order.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{order.status}</span></div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {order.status === 'Pending' && (
                          <>
                            <button
                              className="px-4 py-1 rounded bg-green-500 text-white hover:bg-green-600 transition"
                              onClick={() => handleRentalStatus(order._id, 'Approved')}
                              disabled={updatingOrderId === order._id}
                            >
                              {updatingOrderId === order._id ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              className="px-4 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition"
                              onClick={() => handleRentalStatus(order._id, 'Rejected')}
                              disabled={updatingOrderId === order._id}
                            >
                              {updatingOrderId === order._id ? 'Rejecting...' : 'Reject'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === 'myRentalSubmissions' && (
            <div className="w-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2"><span className="text-indigo-500">📝</span>My Rental Submissions</h3>
              {loadingSubmissions ? (
                <div className="flex flex-col items-center justify-center h-32 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-600"></div>
                </div>
              ) : submissionsError ? (
                <div className="flex flex-col items-center justify-center h-32 w-full bg-red-50 rounded-xl shadow-inner">
                  <span className="text-3xl text-red-400 mb-2">⚠️</span>
                  <p className="text-red-500 font-medium">{submissionsError}</p>
                </div>
              ) : myRentalSubmissions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 w-full bg-gray-50 rounded-xl shadow-inner">
                  <span className="text-4xl mb-2">📦</span>
                  <p className="text-gray-500 font-medium">You have not submitted any rental items yet.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4 w-full">
                  {myRentalSubmissions.map(item => (
                    <div key={item._id} className="bg-white rounded-xl shadow p-4 flex items-center gap-4 hover:shadow-lg transition">
                      <img src={item.rentalImages?.[0] || '/logo.png'} className="w-16 h-16 rounded-lg object-cover border" alt={item.name} />
                      <div className="flex-1">
                        <div className="font-semibold">{item.name}</div>
                        <div className="text-xs text-gray-500">Price: ₹{item.price}</div>
                        <div className="text-xs text-gray-500">Status: <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${item.rentalStatus === 'approved' ? 'bg-green-100 text-green-700' : item.rentalStatus === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{item.rentalStatus}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Review Modal */}
      {reviewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-pink-500 text-2xl font-bold"
              onClick={closeReviewModal}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Add Review for {reviewingItem?.item?.name}</h3>
            <form onSubmit={handleReviewSubmit} className="space-y-4">
              {/* Star Rating */}
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(star => (
                  <button
                    type="button"
                    key={star}
                    className={star <= reviewRating ? 'text-pink-500' : 'text-gray-300'}
                    onClick={() => setReviewRating(star)}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                  >
                    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" /></svg>
                  </button>
                ))}
              </div>
              {/* Comment */}
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-pink-400"
                rows={3}
                placeholder="Write your review..."
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                required
              />
              {/* Submit */}
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold text-lg shadow hover:from-pink-600 hover:to-indigo-600 transition"
                disabled={submittingReview}
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 